import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, CalendarClock, Leaf, Zap } from "lucide-react";
import api from "../api";
import { useFx } from "../context/FxContext";
import Countdown from "../components/dashboard/Countdown";
import { EmptyState } from "../components/dashboard/EmptyState";
import { isLive } from "../utils/countdown";
import { withBoostOrder } from "../utils/boost";
import { money } from "../utils/currency";
import { useLang } from "../context/LanguageContext";
import { thumb } from "../utils/image";
import { placeLabel } from "../utils/place";

const NearExpiryPage = () => {
  const { approx } = useFx();
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/deals", { params: { near_expiry: true } })
      .then((r) => setItems(withBoostOrder(r.data.filter(isLive))))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6 lp-fade-up">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2"><Clock className="text-orange-500" /> {t("rail.nearExpiry")} <span className="text-rose-500">{t("rail.lastChance")}</span></h1>
        <p className="text-sm text-muted-foreground">{t("nearexp.subtitle")}</p>
      </div>

      <div className="grid gap-4">
        {items.map((it) => {
          const priceBlock = (
            <>
              {it.price != null && (<p><span className="font-extrabold text-foreground text-lg">{money(it.price, it.currency)}</span>{" "}{it.was != null && <span className="text-muted-foreground line-through text-xs">{money(it.was, it.currency)}</span>}</p>)}
              {it.price != null && approx(it.price, it.currency) && (<p className="text-[11px] text-muted-foreground" data-testid="near-expiry-approx">{approx(it.price, it.currency)} approx.</p>)}
            </>
          );
          const grabBtn = (cls) => (
            <Link to={`/deal/${it.id}`} data-testid={`near-expiry-grab-${it.id}`} className={`inline-flex items-center justify-center bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold rounded-xl transition-colors ${cls}`}>{t("nearexp.grab")}</Link>
          );
          return (
            <div key={it.id} className="bg-card border border-border rounded-2xl p-4 shadow-sm" data-testid={`near-expiry-card-${it.id}`}>
              <div className="flex items-start gap-3 sm:gap-4">
                <Link to={`/deal/${it.id}`} className="shrink-0"><img src={thumb(it.image, 160)} alt={it.title} loading="lazy" decoding="async" className="h-20 w-20 rounded-xl object-cover" /></Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/deal/${it.id}`} className="min-w-0"><h3 className="font-bold text-foreground hover:text-[#16a34a] leading-snug line-clamp-2">{it.title}</h3></Link>
                    <span className="shrink-0 bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a] text-xs font-bold px-2 py-1 rounded-md">{it.discount}% OFF</span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{it.business}</p>
                  {it.boostedUntil && new Date(it.boostedUntil) > new Date() && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#b8860b] bg-[#fdf6e3] dark:bg-[#3a2f10] px-2 py-0.5 rounded-md mt-1" data-testid={`near-expiry-boosted-${it.id}`}><Zap size={11} /> {t("card.sponsored")}</span>
                  )}
                  {it.shortDated && (<p className="inline-flex items-center gap-1 text-[11px] font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-2 py-0.5 rounded-md mt-1" data-testid={`near-expiry-short-dated-${it.id}`}><Leaf size={11} /> {t("mydeals.shortDated")}{it.bestBefore ? ` · ${t("mydeals.useBy", { date: new Date(it.bestBefore).toLocaleDateString() })}` : ""}</p>)}
                  <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 truncate"><MapPin size={12} className="text-[#16a34a] shrink-0" /> <span className="truncate">{placeLabel(it.location, it.country)}</span></p>
                  <p className="text-xs text-orange-500 font-semibold mt-1 flex items-center gap-1"><Clock size={12} /> <Countdown expiresAt={it.expiresAt} fallback={it.timeLeft} /></p>
                </div>
                <div className="hidden sm:block text-right shrink-0">
                  {priceBlock}
                  {grabBtn("mt-2 px-4 py-2")}
                </div>
              </div>
              <div className="sm:hidden flex items-center justify-between gap-3 mt-3 pt-3 border-t border-border">
                <div className="min-w-0">{priceBlock}</div>
                {grabBtn("shrink-0 px-5 min-h-[44px]")}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <EmptyState icon={CalendarClock} title={t("nearexp.emptyTitle")} message={t("nearexp.emptyMsg")} action={<Link to="/browse" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{t("nearexp.browseAll")}</Link>} />}
      </div>
    </div>
  );
};

export default NearExpiryPage;
